import React, { type PropsWithChildren } from 'react'

import { useTranslation } from 'react-i18next'

import type { ResumoSimulacao } from '@/features/proposta/entities/ResumoProposta/resumo-simulacoes-proposta'
import type { TPropostaLocationState } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { FluxoPagamentoSheet } from '@/features/simulacao/components/fluxo-pagamento'
import { useFluxoPagamento } from '@/features/simulacao/hooks/use-fluxo-pagamento'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import {
  Button,
  SectionContent,
  SectionGroup,
  SectionHeader,
  SectionItem,
  SectionItemListaValores,
  SectionItemValue,
  SectionTitle,
} from '@/shared/components/ui'
import { useTypedLocation } from '@/shared/hooks/use-typed-location'

type TPropostaCompraContentVerticalProps = {
  resumo: ResumoSimulacao
  vagaIds: TPropostaSchemaInput['informacoesIniciais']['vagaIds']
  plantaDescricao: string
}

type TPropostaCompraContentSacocVParceladoProps = {
  resumo: ResumoSimulacao
}

type TPropostaCompraContentSacocVSaldoProps = {
  resumo: ResumoSimulacao
}

type TPropostaCompraSectionProps = {
  resumo: ResumoSimulacao
  vagaIds: TPropostaSchemaInput['informacoesIniciais']['vagaIds']
  plantaDescricao: string
  onEdit: (step: number) => void
}

type TPropostaCompraWrapperProps = PropsWithChildren<{
  onEdit: (step: number) => void
  resumo: ResumoSimulacao
}>

const STEP_INFORMACOES_INICIAIS = 0

const PropostaCompraWrapper = ({ resumo, children, onEdit }: TPropostaCompraWrapperProps) => {
  const { t } = useTranslation('proposta')
  const { state } = useTypedLocation<TPropostaLocationState>()
  const { data } = useFluxoPagamento(state.simulacoesIds)

  return (
    <div>
      <SectionHeader>
        <SectionTitle>{t('resumo_proposta.proposta_compra.titulo')}</SectionTitle>
        <div className="hidden sm:flex sm:gap-2">
          <Render.If condition={data?.length}>
            {() => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={data ?? []}>
                <Button variant="ghost" className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900">
                  <Icon name="eye-open" className="mr-1 size-4" />
                  {t('resumo_proposta.proposta_compra.fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
          <Button
            variant="ghost"
            className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900"
            onClick={() => onEdit(STEP_INFORMACOES_INICIAIS)}
          >
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionHeader>
      <SectionContent className="sm:pb-0">
        <SectionGroup withBorder>
          <SectionItem label={t('resumo_proposta.proposta_compra.label.empreendimento')} colSpan={6} withRightBorder>
            <SectionItemValue>{resumo.nomeEmpreendimento}</SectionItemValue>
          </SectionItem>
          <SectionItem label={`${resumo.labelBloco} e ${resumo.labelProduto}`} colSpan={6}>
            <SectionItemValue>
              {resumo.blocoEmpreendimento} - {resumo.codigoProdutoPrincipal}
            </SectionItemValue>
          </SectionItem>
        </SectionGroup>

        {children}

        <div className="block sm:hidden">
          <div className="flex flex-col gap-3">
            <Render.If condition={data?.length}>
              {() => (
                <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={data ?? []}>
                  <Button variant="outline" className="w-full">
                    <Icon name="eye-open" className="mr-1 size-4" />
                    {t('resumo_proposta.proposta_compra.fluxo_pagamento')}
                  </Button>
                </FluxoPagamentoSheet>
              )}
            </Render.If>
            <Button variant="outline" className="w-full" onClick={() => onEdit(STEP_INFORMACOES_INICIAIS)}>
              <Icon name="pencil" className="mr-1 size-4" />
              {t('resumo_proposta.editar')}
            </Button>
          </div>
        </div>
      </SectionContent>
    </div>
  )
}

const PropostaCompraContentVertical = ({ resumo, vagaIds, plantaDescricao }: TPropostaCompraContentVerticalProps) => {
  const { t } = useTranslation('proposta')
  const vagas = vagaIds.some((v) => !v.substituiVagaPadrao) ? vagaIds.map((v) => v.descricao).join(', ') : '-'

  return (
    <React.Fragment>
      <SectionGroup withBorder>
        <SectionItem label={resumo.labelProduto} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.codigoProdutoPrincipal}</SectionItemValue>
        </SectionItem>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.vaga_padrao')} colSpan={6}>
          <SectionItemValue>{vagas}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.acessorio')}>
          <SectionItemValue>{resumo.codigosAcessorios}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.marcacao_alvenaria')} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.dataMarcacaoAlvenaria}</SectionItemValue>
        </SectionItem>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.planta')} colSpan={6}>
          <SectionItemValue>{plantaDescricao || '-'}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.valor_total_proposta')}>
          <SectionItemValue>{resumo.valorProposta}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.poupanca')} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.valorPoupanca}</SectionItemValue>
        </SectionItem>

        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.entrada')}
          valores={resumo.entrada}
          colSpan={6}
        />
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.mensais')}
          valores={resumo.mensais}
          colSpan={6}
          withRightBorder
        />
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.intermediarias')}
          valores={resumo.intermediarias}
          colSpan={6}
        />
      </SectionGroup>

      <SectionGroup>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.saldo')} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.valorSaldo}</SectionItemValue>
        </SectionItem>
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.pos_conclusao')}
          valores={resumo.posConclusao}
          colSpan={6}
        />
      </SectionGroup>
    </React.Fragment>
  )
}

const PropostaCompraContentSacocVParcelado = ({ resumo }: TPropostaCompraContentSacocVParceladoProps) => {
  const { t } = useTranslation('proposta')

  return (
    <React.Fragment>
      <SectionGroup withBorder>
        <SectionItem
          label={t('resumo_proposta.proposta_compra.label.valor_total_proposta')}
          colSpan={6}
          withRightBorder
        >
          <SectionItemValue>{resumo.valorProposta}</SectionItemValue>
        </SectionItem>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.forma_de_pagamento')} colSpan={6}>
          <SectionItemValue>{resumo.formaDePagamento}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.poupanca')} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.valorPoupanca}</SectionItemValue>
        </SectionItem>

        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.entrada')}
          valores={resumo.entrada}
          colSpan={6}
        />
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.mensais')}
          valores={resumo.mensais}
          colSpan={6}
          withRightBorder
        />
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.intermediarias')}
          valores={resumo.intermediarias}
          colSpan={6}
        />
      </SectionGroup>

      <SectionGroup>
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.pos_conclusao')}
          valores={resumo.posConclusao}
          colSpan={6}
          withRightBorder
        />
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.intermediarias_pos_conclusao')}
          valores={resumo.intermediariasPosConclusao}
          colSpan={6}
        />
      </SectionGroup>
    </React.Fragment>
  )
}

const PropostaCompraContentSacocVSaldo = ({ resumo }: TPropostaCompraContentSacocVSaldoProps) => {
  const { t } = useTranslation('proposta')

  return (
    <React.Fragment>
      <SectionGroup withBorder>
        <SectionItem
          label={t('resumo_proposta.proposta_compra.label.valor_total_proposta')}
          colSpan={6}
          withRightBorder
        >
          <SectionItemValue>{resumo.valorProposta}</SectionItemValue>
        </SectionItem>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.forma_de_pagamento')} colSpan={6}>
          <SectionItemValue>{resumo.formaDePagamento}</SectionItemValue>
        </SectionItem>
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.poupanca')} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.valorPoupanca}</SectionItemValue>
        </SectionItem>

        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.entrada')}
          valores={resumo.entrada}
          colSpan={6}
        />
      </SectionGroup>

      <SectionGroup withBorder>
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.mensais')}
          valores={resumo.mensais}
          colSpan={6}
          withRightBorder
        />
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.intermediarias')}
          valores={resumo.intermediarias}
          colSpan={6}
        />
      </SectionGroup>

      <SectionGroup>
        <SectionItem label={t('resumo_proposta.proposta_compra.label.saldo')} colSpan={6} withRightBorder>
          <SectionItemValue>{resumo.valorSaldo}</SectionItemValue>
        </SectionItem>
        <SectionItemListaValores
          label={t('resumo_proposta.proposta_compra.label.pos_conclusao')}
          valores={resumo.posConclusao}
          colSpan={6}
        />
      </SectionGroup>
    </React.Fragment>
  )
}

export function PropostaCompraSection({
  resumo,
  vagaIds,
  plantaDescricao,
  onEdit,
}: Readonly<TPropostaCompraSectionProps>) {
  if (resumo.tipoDeAmortizacao === 'SacocV') {
    if (resumo.formaDePagamento === 'Parcelado') {
      return (
        <PropostaCompraWrapper resumo={resumo} onEdit={onEdit}>
          <PropostaCompraContentSacocVParcelado resumo={resumo} />
        </PropostaCompraWrapper>
      )
    }

    return (
      <PropostaCompraWrapper resumo={resumo} onEdit={onEdit}>
        <PropostaCompraContentSacocVSaldo resumo={resumo} />
      </PropostaCompraWrapper>
    )
  }

  return (
    <PropostaCompraWrapper resumo={resumo} onEdit={onEdit}>
      <PropostaCompraContentVertical resumo={resumo} vagaIds={vagaIds} plantaDescricao={plantaDescricao} />
    </PropostaCompraWrapper>
  )
}
